if (!customElements.get('bt-wishlist')) {
  class BtWishlist extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.content = this.querySelector('[data-content]');
      this.status = this.querySelector('[data-status]');
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.currency = this.dataset.currency || 'EUR';
      this.page = 1;
      this.productCache = new Map();

      if (this.dataset.loggedIn !== 'true') {
        this.renderLogin();
        return;
      }

      this.loadPage();
    }

    text(key) {
      return this.dataset[`text${key}`] || '';
    }

    setStatus(message = '') {
      this.status.textContent = message;
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    renderLogin() {
      this.setStatus();
      const panel = this.element('div', 'bt-wishlist__message');
      panel.append(
        this.element('h1', 'bt-wishlist__message-title', this.text('LoginHeading')),
        this.element('p', 'bt-wishlist__message-copy', this.text('LoginBody')),
      );
      const link = this.element('a', 'bt-wishlist__login', this.text('Login'));
      link.href = this.dataset.loginUrl;
      panel.append(link);
      this.content.replaceChildren(panel);
    }

    renderEmpty() {
      this.setStatus();
      const panel = this.element('div', 'bt-wishlist__empty');
      const icon = this.element('span', 'bt-wishlist__empty-icon');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 48 48');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M24 40S7 29.5 7 18.5C7 9 19 6 24 16c5-10 17-7 17 2.5C41 29.5 24 40 24 40Z');
      svg.append(path);
      icon.append(svg);
      const heading = this.element('h1', 'bt-wishlist__empty-title', this.text('EmptyHeading'));
      const body = this.element('p', 'bt-wishlist__empty-copy', this.text('EmptyBody'));
      const home = this.element('a', 'bt-wishlist__home', this.text('Home'));
      home.href = '/';
      panel.append(icon, heading, body, home);
      this.content.replaceChildren(panel);
    }

    renderMessage(heading, body, actionLabel, action) {
      const panel = this.element('div', 'bt-wishlist__message');
      panel.append(
        this.element('h1', 'bt-wishlist__message-title', heading),
        this.element('p', 'bt-wishlist__message-copy', body),
      );
      const button = this.element('button', 'bt-wishlist__retry', actionLabel);
      button.type = 'button';
      button.addEventListener('click', action, { once: true });
      panel.append(button);
      this.content.replaceChildren(panel);
    }

    async loadPage() {
      this.setStatus(this.text('Loading'));
      try {
        const response = await fetch(`/apps/bt-signals/v1/favorites?page=${this.page}&page_size=12`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (response.status === 401) {
          this.renderLogin();
          return;
        }
        if (!response.ok) throw new Error(String(response.status));

        const payload = await response.json();
        const items = payload.data?.items;
        if (!Array.isArray(items)) throw new Error('Invalid favorites response');
        const pagination = payload.data?.pagination || { page: this.page, total_pages: 1, total_items: items.length };

        if (!items.length && this.page > 1) {
          this.page -= 1;
          await this.loadPage();
          return;
        }
        if (!items.length) {
          this.renderEmpty();
          return;
        }

        const cards = await Promise.all(items.map((item) => this.loadProduct(item)));
        this.renderItems(cards, pagination);
        this.setStatus('');
      } catch (_error) {
        this.setStatus('');
        this.renderMessage(this.text('Error'), this.text('Error'), this.text('Retry'), () => this.loadPage());
      }
    }

    async loadProduct(item) {
      if (!item?.product_handle) return { item, product: null };
      if (!this.productCache.has(item.product_handle)) {
        const request = fetch(`/products/${encodeURIComponent(item.product_handle)}.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
        }).then(async (response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        }).catch(() => null);
        this.productCache.set(item.product_handle, request);
      }
      return { item, product: await this.productCache.get(item.product_handle) };
    }

    formatPrice(amount) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount / 100);
      } catch (_error) {
        return `${(amount / 100).toFixed(2)} ${this.currency}`;
      }
    }

    formatSavedDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const formatted = new Intl.DateTimeFormat(this.locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
      return this.text('Favorited').replace('{{ date }}', formatted);
    }

    renderItems(loadedProducts, pagination) {
      const wrapper = document.createDocumentFragment();
      const heading = this.element('header', 'bt-wishlist__heading');
      heading.append(
        this.element('h1', 'bt-wishlist__title', this.dataset.title || document.title),
        this.element('span', 'bt-wishlist__count', String(pagination.total_items ?? loadedProducts.length)),
      );
      const grid = this.element('ul', 'bt-wishlist__grid');

      loadedProducts.forEach(({ item, product }) => {
        const variant = product?.variants?.find((entry) => String(entry.id) === String(item.variant_id));
        grid.append(this.createCard(item, product, variant));
      });

      wrapper.append(heading, grid);
      const totalPages = Number(pagination.total_pages) || 1;
      if (totalPages > 1) wrapper.append(this.createPagination(totalPages));
      this.content.replaceChildren(wrapper);
    }

    createCard(item, product, variant) {
      const card = this.element('li', 'bt-wishlist-card');
      const handle = String(item.product_handle || '');
      const productUrl = `/products/${encodeURIComponent(handle)}`;
      const title = product?.title || handle || this.text('Error');
      const imageUrl = variant?.featured_image?.src || product?.featured_image || product?.images?.[0];
      const imageLink = this.element('a', 'bt-wishlist-card__image-link');
      imageLink.href = productUrl;
      imageLink.setAttribute('aria-label', title);
      if (imageUrl) {
        const image = this.element('img', 'bt-wishlist-card__image');
        image.src = imageUrl;
        image.alt = product?.image?.alt || title;
        image.loading = 'lazy';
        imageLink.append(image);
      }

      const details = this.element('div', 'bt-wishlist-card__details');
      const titleLink = this.element('a', 'bt-wishlist-card__title', title);
      titleLink.href = productUrl;
      details.append(titleLink);
      if (variant && variant.title && variant.title !== 'Default Title') {
        details.append(this.element('p', 'bt-wishlist-card__variant', `${this.text('Variant')}: ${variant.title}`));
      }

      const savedDate = this.formatSavedDate(item.saved_at);
      if (savedDate) details.append(this.element('p', 'bt-wishlist-card__saved', savedDate));
      if (variant) details.append(this.element('p', 'bt-wishlist-card__price', this.formatPrice(variant.price)));
      else details.append(this.element('p', 'bt-wishlist-card__price', this.text('Error')));

      const actions = this.element('div', 'bt-wishlist-card__actions');
      const add = this.element('button', 'bt-wishlist-card__add', variant?.available ? this.text('Add') : this.text('SoldOut'));
      add.type = 'button';
      add.disabled = !variant?.available;
      if (variant?.available) add.addEventListener('click', () => this.addToCart(variant.id, add));
      const remove = this.element('button', 'bt-wishlist-card__remove', this.text('Remove'));
      remove.type = 'button';
      remove.setAttribute('aria-label', `${this.text('Remove')} ${title}`);
      remove.addEventListener('click', () => this.removeFavorite(item.variant_id, remove));
      actions.append(add, remove);
      details.append(actions);
      card.append(imageLink, details);
      return card;
    }

    createPagination(totalPages) {
      const pagination = this.element('nav', 'bt-wishlist__pagination');
      pagination.setAttribute('aria-label', this.text('Page'));
      const previous = this.element('button', '', this.text('Previous'));
      previous.type = 'button';
      previous.disabled = this.page <= 1;
      previous.addEventListener('click', () => {
        this.page -= 1;
        this.loadPage();
      });
      const pageLabel = this.element('span', '', `${this.text('Page')} ${this.page} / ${totalPages}`);
      const next = this.element('button', '', this.text('Next'));
      next.type = 'button';
      next.disabled = this.page >= totalPages;
      next.addEventListener('click', () => {
        this.page += 1;
        this.loadPage();
      });
      pagination.append(previous, pageLabel, next);
      return pagination;
    }

    async removeFavorite(variantId, button) {
      button.disabled = true;
      try {
        const response = await fetch(`/apps/bt-signals/v1/favorites/${encodeURIComponent(variantId)}`, {
          method: 'DELETE',
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (response.status === 401) {
          this.renderLogin();
          return;
        }
        if (!response.ok) throw new Error(String(response.status));
        await this.loadPage();
      } catch (_error) {
        button.disabled = false;
        this.setStatus(this.text('Error'));
      }
    }

    async addToCart(variantId, button) {
      button.disabled = true;
      try {
        const cart = document.querySelector('cart-drawer, cart-notification');
        const sections = cart?.getSectionsToRender().map((section) => section.id) || [];
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('Cart request failed');
        const addedProductId = result.product_id || result.items?.[0]?.product_id;
        if (addedProductId) {
          window.dispatchEvent(new CustomEvent('bt:signals:cart-add', { detail: { productId: addedProductId } }));
        }
        if (cart?.renderContents) cart.renderContents(result);
        else window.location.assign('/cart');
      } catch (_error) {
        button.disabled = false;
        this.setStatus(this.text('CartError'));
      }
    }
  }

  customElements.define('bt-wishlist', BtWishlist);
}